import { type JSX, useState } from 'react';

import { buildQuery, useOptionalNavigator } from './router';

/**
 * The marketplace search entry.
 *
 * ## It goes somewhere real now
 *
 * The header search was removed from the shell because `/search` did not exist
 * and submitting bounced the customer back to the homepage with their query
 * dropped. `/search` exists, so a search box can exist — and this one navigates
 * through the router rather than reloading, so the input survives.
 *
 * ## Outside a router it is an ordinary form
 *
 * `action="/search"` with a GET method, so submitting works with no JavaScript
 * at all and works in a tree with no router — the browser loads `/search` with
 * the query string attached, which is now a real page. Inside a router the
 * submit is intercepted so the input survives and nothing reloads.
 *
 * ## Location is typed, never taken
 *
 * A postcode field, optional, empty by default. Nothing here calls
 * `navigator.geolocation`: a marketplace does not need to know where somebody
 * is standing to show them a pharmacy, and prompting on a first visit asks for
 * trust the page has not earned.
 */
export function SearchBar({
  initialQuery = '',
  initialPostcode = '',
  autoFocus = false,
}: {
  readonly initialQuery?: string;
  readonly initialPostcode?: string;
  readonly autoFocus?: boolean;
}): JSX.Element {
  const navigator = useOptionalNavigator();
  const [query, setQuery] = useState(initialQuery);
  const [postcode, setPostcode] = useState(initialPostcode);

  return (
    <form
      className="jt-searchbar"
      role="search"
      aria-label="Search the marketplace"
      method="get"
      action="/search"
      onSubmit={(event) => {
        // No router: let the browser submit to /search, which is a real page.
        if (navigator === undefined) return;

        event.preventDefault();
        navigator.navigate(`/search${buildQuery({ q: query.trim(), postcode: postcode.trim() })}`);
      }}
    >
      <div className="jt-searchbar__field">
        <label className="jt-visually-hidden" htmlFor="jt-search-q">
          What are you looking for?
        </label>
        <input
          id="jt-search-q"
          name="q"
          type="search"
          className="jt-input"
          placeholder="Search medicines, health tests, treatments"
          value={query}
          autoFocus={autoFocus}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      <div className="jt-searchbar__field jt-searchbar__field--narrow">
        <label className="jt-visually-hidden" htmlFor="jt-search-postcode">
          Your postcode, optional
        </label>
        <input
          id="jt-search-postcode"
          name="postcode"
          type="text"
          className="jt-input"
          placeholder="Postcode (optional)"
          autoComplete="postal-code"
          inputMode="text"
          maxLength={8}
          value={postcode}
          onChange={(event) => setPostcode(event.target.value)}
        />
      </div>

      <button type="submit" className="jt-button jt-button--primary jt-searchbar__submit">
        Search
      </button>
    </form>
  );
}
